"use client";
import React, { useState, useEffect } from "react";

// import { EventsSection } from "@/components/Sections/EventsSection";
import { ChevronDown } from "lucide-react";
import EventsListing from "./EventCard";
import { DropdownOptions, Event } from "@/types/common";
import Link from "next/link";
import { pageService } from "@/services/pageServices";
import MobileNav from "@/components/ui/MobileNav";
import { padding } from "@/services/commonFunction";
import Loader from "@/components/Loader";
import YearSidebar from "./YearSidebar";
import MobileFilters from "./MobileFilters";
export default function EventListingPage({
  title,
  time = "all",
}: {
  title: string;
  time?: string;
}) {
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  // const [selectedYear, setSelectedYear] = useState<number>(2025);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [infiniteLoading, setInfiniteLoading] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [yearEventCounts, setYearEventCounts] = useState<
    { year: number; totalEvents: number }[]
  >([]);
  const [sortBy, setSortBy] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const [sortOptions, setSortOptions] = useState<DropdownOptions[]>([]);
  const [eventSortOptions, setEventSortOptions] = useState<DropdownOptions[]>(
    []
  );
  const [yearEvent, setYearEvent] = useState<DropdownOptions[]>([]);
  const [selectedEventTag, setSelectedEventTag] = useState<string>("");

  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const generateAllDropdownOptions = (
    events: Event[],
    yearCounts: { year: number; totalEvents: number }[]
  ) => {
    const uniqueTypes = Array.from(
      new Set(
        events
          .map((e) => e.event_type)
          .filter((type): type is string => typeof type === "string")
      )
    );

    const uniqueTags = Array.from(
      new Set(events.map((e) => e.tag).filter(Boolean))
    );

    const uniqueYears = Array.from(new Set(yearCounts.map((y) => y.year))).sort(
      (a, b) => b - a
    );

    return {
      sortOptions: [
        { label: "All", value: "" },
        ...uniqueTypes.map((type) => ({ label: type, value: type })),
      ],
      eventSortOptions: [
        { label: "All", value: "" },
        ...uniqueTags.map((tag) => ({
          label: tag === "past" ? "Previous Events" : "Upcoming Events",
          value: tag === "past" ? "past" : "upcoming",
        })),
      ],
      yearEvent: uniqueYears.map((year) => ({
        label: year.toString(),
        value: year.toString(),
      })),
    };
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleSortChange = (option: { label: string; value: string }) => {
    setSortBy(option.value);
  };

  const handleYearSelect = (value: string) => {
    setSelectedYear(parseInt(value));
  };

  const [maxShowYear, setMaxShowYear] = useState(6);


  const loadEvents = async (
    pageNum: number = 1,
    isInitial: boolean = false
  ) => {
    try {
      if (isInitial && isFirstLoad) {
        setLoading(true);
      } else if (isInitial) {
        setEventsLoading(true);
      } else {
        setInfiniteLoading(true);
      }

      const response = await pageService.getEvents(
        // selectedEventTag === "upcoming"
        //   ? false
        //   : selectedEventTag === "past"
        //   ? true
        //   : archived,
        time,
        selectedYear.toString(),
        "",
        pageNum
      );

      const newEvents = response.data.data;

      if (isInitial || pageNum === 1) {
        setAllEvents(newEvents);
        setPage(1);
      } else {
        setAllEvents((prev) => [...prev, ...newEvents]);
      }

      setTotalPages(response.data.meta.totalPages);
      setHasMore(pageNum < response.data.meta.totalPages);
      if (isInitial) {
        const { sortOptions, eventSortOptions, yearEvent } =
          generateAllDropdownOptions(
            isInitial || pageNum === 1
              ? newEvents
              : [...allEvents, ...newEvents],
            response.data.yearEventCounts
          );

        // setSortOptions(sortOptions);
        setEventSortOptions(eventSortOptions);
        setYearEvent(yearEvent);
        setYearEventCounts(response.data.yearEventCounts || []);
        // setSelectedYear(Number(response?.data?.yearEventCounts[0]?.year));

        if (response.data.yearEventCounts?.length > 0) {
          setMaxShowYear(time === "past" ? response.data.yearEventCounts.length : 6);
        } else {
          setMaxShowYear(0);
        }
      }
    } catch (error) {
      console.error("Error loading events:", error);
      setError("Failed to load events");
    } finally {
      if (isInitial && isFirstLoad) {
        setLoading(false);
        setIsFirstLoad(false);
      } else if (isInitial) {
        setEventsLoading(false);
      } else {
        setInfiniteLoading(false);
      }
    }
  };

  const fetchMoreEvents = () => {
    if (hasMore && !loading && !eventsLoading && !infiniteLoading) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadEvents(nextPage, false);
    }
  };

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    loadEvents(1, true);
  }, [selectedYear, selectedEventTag, time]);

  useEffect(() => {
    const filtered = allEvents.filter(
      (event) =>
        (sortBy === "" || event.event_group_id === sortBy) &&
        (selectedYear === 0 || event.year === selectedYear) &&
        (selectedEventTag === "" || event.tag === selectedEventTag)
    );
    
    setEvents(filtered);
  }, [sortBy, selectedYear, selectedEventTag, allEvents]);

  useEffect(() => {
    fetchFilterBy()
  }, []);

  const fetchFilterBy = async () => {
    try {
      const response = await pageService.getEventsFilterBy()
      if (response?.data?.data) {
        setSortOptions(response?.data?.data)
      }

      
    } catch (error) {
      console.log("error", error);
      
    }
  }
  

  if (loading) return <Loader />;

  if (error) {
    return (
      <div className="text-red-500 text-center h-[300px] flex items-center justify-center">
        {error}
      </div>
    );
  }

  return (
    <div className={`max-w-7xl mx-auto px-7 md:px-4 lg:px-8 xl:px-0 ${padding.py}`}>
      {/* <pre className="text-wrap">{JSON.stringify(allEvents,null,2)}</pre> */}
      <MobileFilters
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
        toggleDropdown={toggleDropdown}
        handleSortChange={handleSortChange}
        yearEventCounts={yearEventCounts}
        selectedYear={selectedYear}
        // onYearSelect={handleYearSelect}
        maxShowYear={maxShowYear}
        time={time}
        setSelectedYear={setSelectedYear}
        yearEvent={yearEvent}
        isDropdownOpen={isDropdownOpen}
        handleYearSelect={handleYearSelect}
      />
      <div className="flex flex-col md:flex-row gap-8 pt-10 w-full h-full">
        {/* Left Sidebar */}
        <YearSidebar
          yearEventCounts={yearEventCounts}
          selectedYear={selectedYear}
          onYearSelect={handleYearSelect}
          maxShowYear={maxShowYear}
          time={time}
          setSelectedYear={setSelectedYear}
        />

        {/* <EventsListing
          data={{
            title: title,
            events: events,
            type: "section",
            template: "events_section",
            description: "",
            image: "",
          }}
        /> */}

        {eventsLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <Loader />
          </div>
        ) : (
          <EventsListing
            data={{
              title: title,
              events: events,
              type: "section",
              template: "events_section",
              description: "",
              image: "",
            }}
            hasMore={hasMore}
            fetchMore={fetchMoreEvents}
          />
        )}
      </div>
    </div>
  );
}
